// src/pages/customer/Register.jsx
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useAuth } from "../../context/AuthContext";
import Spinner from "../../components/common/Spinner";
import toast from "react-hot-toast";
import { FiMail, FiLock, FiUser, FiPhone, FiEye, FiEyeOff } from "react-icons/fi";
import logo from "../../assests/logo.png";

export default function Register() {
  const { register: registerFn } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const { register, handleSubmit, watch, formState: { errors } } = useForm();

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      await registerFn(data);
      toast.success("Account created! Welcome to Tripura Agro.");
      navigate("/");
    } catch (err) {
      toast.error(err.code === "auth/email-already-in-use" ? "Email already registered" : "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const inputClass = (hasError) => `
    w-full rounded-xl border bg-gray-50 text-sm text-gray-900 placeholder-gray-400
    pl-9 pr-3 py-2.5 outline-none transition-all
    focus:bg-white focus:border-primary-400 focus:ring-2 focus:ring-primary-100
    hover:border-gray-300
    ${hasError ? "border-red-300 bg-red-50 focus:border-red-400 focus:ring-red-100" : "border-gray-200"}
  `;

  return (
    <div className="min-h-[calc(100vh-200px)] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">

        {/* Card */}
        <div className="card p-7">
  <h1 className="text-2xl font-bold text-gray-900">
    Create Account
  </h1>

  <p className="text-gray-500 mt-2 text-sm mb-4">
    Join Tripura Agro and get access to fresh agricultural products directly from farmers.
  </p>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">

            {/* Full Name */}
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Full Name *</label>
              <div className="relative">
                <FiUser className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                <input {...register("name", { required: "Name is required" })}
                  type="text" placeholder="John Doe"
                  className={inputClass(errors.name)} />
              </div>
              {errors.name && <p className="text-xs text-red-500 mt-1">⚠ {errors.name.message}</p>}
            </div>

            {/* Email */}
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Email *</label>
              <div className="relative">
                <FiMail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                <input {...register("email", { required: "Email is required", pattern: { value: /^\S+@\S+$/, message: "Invalid email" } })}
                  type="email" placeholder="you@example.com"
                  className={inputClass(errors.email)} />
              </div>
              {errors.email && <p className="text-xs text-red-500 mt-1">⚠ {errors.email.message}</p>}
            </div>

            {/* Phone */}
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Phone *</label>
              <div className="relative">
                <FiPhone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                <input {...register("phone", { required: "Phone is required", pattern: { value: /^[0-9]{10}$/, message: "Enter valid 10-digit number" } })}
                  type="tel" placeholder="9876543210"
                  className={inputClass(errors.phone)} />
              </div>
              {errors.phone && <p className="text-xs text-red-500 mt-1">⚠ {errors.phone.message}</p>}
            </div>

            {/* Password */}
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Password *</label>
              <div className="relative">
                <FiLock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                <input {...register("password", { required: "Password is required", minLength: { value: 6, message: "Min 6 characters" } })}
                  type={showPassword ? "text" : "password"} placeholder="••••••••"
                  className={`${inputClass(errors.password)} pr-10`} />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  aria-label={showPassword ? "Hide password" : "Show password"}>
                  {showPassword ? <FiEyeOff className="w-4 h-4" /> : <FiEye className="w-4 h-4" />}
                </button>
              </div>
              {errors.password && <p className="text-xs text-red-500 mt-1">⚠ {errors.password.message}</p>}
            </div>

            {/* Confirm Password */}
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Confirm Password *</label>
              <div className="relative">
                <FiLock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                <input {...register("confirm", { required: "Please confirm your password", validate: (v) => v === watch("password") || "Passwords do not match" })}
                  type={showConfirm ? "text" : "password"} placeholder="••••••••"
                  className={`${inputClass(errors.confirm)} pr-10`} />
                <button type="button" onClick={() => setShowConfirm(!showConfirm)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  aria-label={showConfirm ? "Hide password" : "Show password"}>
                  {showConfirm ? <FiEyeOff className="w-4 h-4" /> : <FiEye className="w-4 h-4" />}
                </button>
              </div>
              {errors.confirm && <p className="text-xs text-red-500 mt-1">⚠ {errors.confirm.message}</p>}
            </div>

            {/* Submit */}
            <button type="submit" disabled={loading}
              className="btn-primary w-full flex items-center justify-center gap-2 py-2.5 rounded-xl font-semibold text-sm mt-2 disabled:opacity-70 disabled:cursor-not-allowed">
              {loading ? <><Spinner size="sm" /> Creating account...</> : "Create Account"}
            </button>
          </form>

          <p className="text-center text-sm text-gray-600 mt-5">
            Already have an account?{" "}
            <Link to="/login" className="text-primary-600 font-medium hover:underline">Sign in</Link>
          </p>
        </div>

      </div>
    </div>
  );
}